import { Dialog } from "@mui/material";
import * as React from "react";

import { PageLayout } from "@/shared/components";
import { AddToCollection } from "@/shared/components/collections";
import { FilesList, useDownloadFilesOption } from "@/shared/components/files";
import { type SelectionOption } from "@/shared/components/ResourceView";
import { FileRecord } from "@/shared/domain/files";
import { useDeleteFilesMutation } from "@/shared/domain/files/hooks";
import {
  DeleteOutlineIcon,
  PlaylistAddIcon,
} from "@/shared/foundation/icons/mui";
import { usePopupState } from "@/shared/hooks/usePopupState";

/** Rows a bulk action targets, plus the callback that clears the selection on completion. */
interface PendingCollectionAdd {
  ids: string[];
  clearSelection: () => void;
}

export function FilesPage() {
  const { mutate: deleteFiles } = useDeleteFilesMutation();
  const addToCollectionPopup = usePopupState<PendingCollectionAdd>();
  const pendingAdd = addToCollectionPopup.metadata;
  const download = useDownloadFilesOption();

  const selectionOptions = React.useMemo<SelectionOption<FileRecord>[]>(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          addToCollectionPopup.open({
            metadata: { ids: selected.map((f) => f.file_id), clearSelection },
          });
        },
      },
      download,
      {
        name: "Delete files",
        icon: <DeleteOutlineIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          if (selected.length === 0) {
            return;
          }
          const ok = window.confirm(
            `Delete ${selected.length} file${selected.length === 1 ? "" : "s"}? This cannot be undone.`,
          );
          if (!ok) {
            return;
          }
          deleteFiles(
            {
              fileIds: selected.map((file) => file.file_id),
              resourceOwnerId: selected[0].org_id,
            },
            { onSuccess: clearSelection },
          );
        },
      },
    ],
    [addToCollectionPopup, deleteFiles, download],
  );

  return (
    <PageLayout title="Roboto - Files" headerContentLeft="Files">
      <FilesList scope={{ type: "org" }} selectionOptions={selectionOptions} />
      <Dialog
        open={addToCollectionPopup.isOpen}
        onClose={addToCollectionPopup.close}
      >
        <AddToCollection
          selected={{
            type: "File",
            items: new Set(pendingAdd?.ids ?? []),
          }}
          onCancel={addToCollectionPopup.close}
          onSuccess={() => pendingAdd?.clearSelection()}
        />
      </Dialog>
    </PageLayout>
  );
}
